import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  TextField,
  Typography,
  Autocomplete,
  Chip,
} from "@mui/material";
import { DatePicker, TimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ro } from "date-fns/locale";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import moment from "moment";
import genericStyles from "../Styles/genericStyles.module.css";
import styles from "../Styles/smsClientsPage.module.css";
import { Vocabulary } from "../Utils/Vocabulary";
import { endpoints } from "../Utils/UrlEnum";
import Config from "../Utils/Config";
import { postData } from "../Services/postData";
import { ETableEditAction } from "../Utils/Enums";
import MUIDataTableCustomStyle from "../Components/MUIDataTableCustomStyle";
import ExamClientReviewPanel from "../Components/ExamClientReviewPanel";
import GenericModal from "../Components/GenericModal";
import DropzoneComponent from "../Components/DropzoneComponent";
import useCustomMemo from "../Hooks/useCustomMemo";
import withRole from "../Hooks/withRole";
import {
  getFullName,
  setFontColorBasedOnBackground,
  fetchProducts,
} from "../Utils/Utils";
import { getFromCacheOrFetch } from "../Utils/getFromCacheOrFetch";
import { IClientTimeEntry } from "../Utils/Types";

function SmsClientsPage() {
  const memo = useCustomMemo();
  const workPoints = memo?.storage?.workPoints || [];
  const [products, setProducts] = useState<any[]>([]);

  const [selectedWorkPoint, setSelectedWorkPoint] = useState<any>(null);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [smsMessage, setSmsMessage] = useState("");
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [triggerReload, setTriggerReload] = useState<boolean | undefined>();
  const [examFileModalOpen, setExamFileModalOpen] = useState(false);
  const [examFile, setExamFile] = useState<any[]>([]);
  const [isExamMessage, setIsExamMessage] = useState(false);
  const [examTime, setExamTime] = useState<Date | null>(null);
  const [clientIdTimeMap, setClientIdTimeMap] = useState<IClientTimeEntry[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const buildFilters = useCallback(() => {
    const nextFilters: Record<string, any> = {};

    if (selectedWorkPoint?._id) {
      nextFilters.workPointId = selectedWorkPoint._id;
    }

    if (selectedService?._id) {
      nextFilters.productId = selectedService._id;
    }

    if (fromDate) {
      nextFilters.start = moment(fromDate).format(Config.momentUSDateFormat);
    }

    if (toDate) {
      nextFilters.end = moment(toDate).format(Config.momentUSDateFormat);
    }

    const searchValue = new URLSearchParams(window.location.search).get(
      "search"
    );
    if (searchValue) {
      nextFilters.search = searchValue;
    }

    return nextFilters;
  }, [fromDate, selectedService, selectedWorkPoint, toDate]);

  const handleApplyFilters = useCallback(() => {
    const nextFilters = buildFilters();
    setFilters(nextFilters);
    setTriggerReload((prev) => !prev);
  }, [buildFilters]);

  useEffect(() => {
    let mounted = true;

    getFromCacheOrFetch<any[]>({
      cacheKey: "products",
      storage: memo.storage,
      setStorage: memo.setStorageData,
      fetcher: fetchProducts(),
      isValid: (data) => Array.isArray(data) && data.length > 0,
    }).then((data) => {
      if (mounted) {
        setProducts(data);
      }
    });

    return () => {
      mounted = false;
    };
  }, [memo]);

  const handleSendSms = useCallback(async () => {
    try {
      if (!smsMessage.trim()) {
        return;
      }

      const filterPayload = buildFilters();

      await postData(endpoints.notifications.sendSMSFiltered, {
        message: smsMessage,
        filters: filterPayload,
      });

      setSmsMessage("");
    } catch (error) {
      console.error("Error sending SMS:", error);
    }
  }, [buildFilters, smsMessage]);

  const clientsColumns = useMemo(
    () => [
      {
        label: ` `,
        name: "_id",
        options: {
          sort: false,
          display: false,
        },
      },
      {
        label: `${Vocabulary.status}`,
        name: "status",
        options: {
          setCellHeaderProps: () => ({
            align: "center",
          }),
          setCellProps: () => ({
            align: "center",
          }),
          filter: false,
          sort: false,
          empty: true,
          customBodyRender: (rowData: any) => {
            return (
              <Chip
                color="primary"
                key={rowData?._id}
                label={rowData?.name}
                style={{
                  margin: 5,
                  backgroundColor: rowData?.color,
                  color: setFontColorBasedOnBackground(rowData?.color),
                }}
              />
            );
          },
        },
      },
      {
        label: `${Vocabulary.instructor}`,
        name: "instructor_id",
        options: {
          sort: false,
          customBodyRender: (rowData: any) => {
            return getFullName(rowData?.user_id);
          },
        },
      },
      {
        label: `${Vocabulary.CNP}`,
        name: "client_id",
        options: {
          sort: false,
          customBodyRender: (rowData: any) => {
            return rowData?.CNP;
          },
        },
      },
      {
        label: `${Vocabulary.clientName}`,
        name: "client_id",
        options: {
          sort: false,
          customBodyRender: (rowData: any) => {
            return (
              <div>
                {`${rowData?.lastName} ${rowData?.firstName} ${
                  !rowData?.lastName && !rowData?.firstName ? "-" : ""
                }`}
              </div>
            );
          },
        },
      },
      {
        label: `${Vocabulary.phone}`,
        name: "client_id",
        options: {
          sort: false,
          customBodyRender: (client: any) => {
            return (
              <>
                {client ? (
                  <a
                    key={client.phone}
                    href={`tel:${client.phone}`}
                    style={{ textDecoration: "none" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Chip
                      icon={<LocalPhoneIcon fontSize="small" />}
                      label={client.phone}
                      style={{ padding: 5 }}
                    />
                  </a>
                ) : null}
              </>
            );
          },
        },
      },
      {
        label: `${Vocabulary.service}`,
        name: "product",
        options: {
          sort: false,
          customBodyRender: (rowData: any) => {
            return <div>{rowData?.name}</div>;
          },
        },
      },
      {
        label: `${Vocabulary.workPoint}`,
        name: "client_id",
        options: {
          sort: false,
          customBodyRender: (rowData: any) => {
            return rowData?.workPointId?.name;
          },
        },
      },
      {
        label: `${Vocabulary.date}`,
        name: "createdAt",
        options: {
          sort: false,
          customBodyRender: (rowData: any) => {
            return moment(rowData).format(Config.momentEUDateFormat);
          },
        },
      },
    ],
    []
  );

  const canSend = smsMessage.trim().length > 0;

  const abbreviateAddress = (text: string) =>
    text
      .replace(/\bBulevardul\b/gi, "Bd.")
      .replace(/\bBulevard\b/gi, "Bd.")
      .replace(/\bStrada\b/gi, "Str.")
      .replace(/\bNumărul\b/gi, "nr.")
      .replace(/\bNumarul\b/gi, "nr.")
      .replace(/\bJudețul\b/gi, "jud.")
      .replace(/\bJudetul\b/gi, "jud.")
      .replace(/\bMunicipiul\b/gi, "Mun.")
      .replace(/\bMunicipiu\b/gi, "Mun.")
      .replace(/\bAleea\b/gi, "Al.")
      .replace(/\bCalea\b/gi, "Cal.")
      .replace(/\bPiața\b/gi, "Pța.")
      .replace(/\bPiata\b/gi, "Pța.")
      .replace(/\bSplaidul\b/gi, "Spl.")
      .replace(/\bIntrarea\b/gi, "Intr.")
      .replace(/\bSectorul\b/gi, "Sect.")
      .replace(/\bSector\b/gi, "Sect.");

  const handleVerifyClients = useCallback(async () => {
    if (!examFile.length) return;
    const formData = new FormData();
    formData.append("file", examFile[0]);
    setIsVerifying(true);
    const response = await postData(endpoints.exams.verifySchedule, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    setIsVerifying(false);

    if (response?.data) {
      const { address, dateAndCounty, clientIdTimeMap: newTimeMap } = response.data;
      const dateMatch = dateAndCounty?.match(/(\d{2}\.\d{2}\.\d{4})/);
      const countyMatch = dateAndCounty?.match(/JUDEȚUL\s+(\S+)/i);
      const date = dateMatch ? dateMatch[1] : "";
      const county = countyMatch ? countyMatch[1] : "";
      setSmsMessage(
        `Examen atestat, ${date} ora {ORA}, jud. ${county}. ${abbreviateAddress(address)}`.slice(0, 160)
      );
      setIsExamMessage(true);
      setExamTime(null);
      if (Array.isArray(newTimeMap)) {
        setClientIdTimeMap(newTimeMap);
      }
    }

    setExamFileModalOpen(false);
  }, [examFile]);

  const handleSendSmsWithTime = useCallback(async () => {
    if (!smsMessage.trim() || !examTime) return;

    const timeStr = moment(examTime).format("HH:mm");
    const messageWithTime = smsMessage.replace("{ORA}", timeStr);
    const filterPayload = buildFilters();

    try {
      await postData(endpoints.notifications.sendSMSFiltered, {
        message: messageWithTime,
        filters: filterPayload,
      });
      setSmsMessage("");
      setIsExamMessage(false);
      setExamTime(null);
    } catch (error) {
      console.error("Error sending SMS:", error);
    }
  }, [buildFilters, examTime, smsMessage]);

  const handleSendSmsGroupedByTime = useCallback(async () => {
    if (!smsMessage.trim() || !clientIdTimeMap.length) return;

    const seenClientIds = new Set<string>();
    const grouped = clientIdTimeMap.reduce<Record<string, string[]>>(
      (acc, { clientId, time }) => {
        if (seenClientIds.has(clientId)) return acc;
        seenClientIds.add(clientId);
        if (!acc[time]) acc[time] = [];
        acc[time].push(clientId);
        return acc;
      },
      {}
    );

    setIsSending(true);
    const results = await Promise.allSettled(
      Object.entries(grouped).map(([time, clientIds]) =>
        postData(endpoints.notifications.sendSMSMessages, {
          message: smsMessage.replace("{ORA}", time),
          clientIds,
          intendedFor: Vocabulary.smsIntendedForExamFile,
        })
      )
    );
    setIsSending(false);

    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length) {
      console.error(`${failed.length} group(s) failed to send.`);
    }

    setSmsMessage("");
    setIsExamMessage(false);
    setClientIdTimeMap([]);
    setTriggerReload((prev) => !prev);
  }, [smsMessage, clientIdTimeMap]);

  return (
    <div className={genericStyles.mainContainer}>
      <div className={styles.container}>
        <div className={styles.leftPanel}>
          <Typography variant="h6" className={styles.sectionTitle}>
            {Vocabulary.smsClientsTitle}
          </Typography>
          <Divider className={styles.sectionDivider} />

          <Box className={styles.fieldGroup}>
            <Typography className={styles.fieldLabel}>
              {Vocabulary.workPoints}
            </Typography>
            <Autocomplete
              options={workPoints}
              value={selectedWorkPoint}
              onChange={(_, value) => setSelectedWorkPoint(value)}
              getOptionLabel={(option: any) => option?.name || ""}
              renderInput={(params) => (
                <TextField {...params} placeholder={Vocabulary.select} />
              )}
              isOptionEqualToValue={(option: any, value: any) =>
                option?._id === value?._id
              }
            />
          </Box>

          <Box className={styles.fieldGroup}>
            <Typography className={styles.fieldLabel}>
              {Vocabulary.service}
            </Typography>
            <Autocomplete
              options={products}
              value={selectedService}
              onChange={(_, value) => setSelectedService(value)}
              getOptionLabel={(option: any) => option?.name || ""}
              renderInput={(params) => (
                <TextField {...params} placeholder={Vocabulary.select} />
              )}
              isOptionEqualToValue={(option: any, value: any) =>
                option?._id === value?._id
              }
            />
          </Box>

          <Box className={styles.fieldGroup}>
            <Typography className={styles.fieldLabel}>
              {Vocabulary.fromDate}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
              <DatePicker
                value={fromDate}
                onChange={(date) => setFromDate(date)}
                format="dd-MM-yyyy"
                slotProps={{
                  textField: { size: "medium", fullWidth: true },
                }}
              />
            </LocalizationProvider>
          </Box>

          <Box className={styles.fieldGroup}>
            <Typography className={styles.fieldLabel}>
              {Vocabulary.toDate}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
              <DatePicker
                value={toDate}
                onChange={(date) => setToDate(date)}
                format="dd-MM-yyyy"
                slotProps={{
                  textField: { size: "medium", fullWidth: true },
                }}
              />
            </LocalizationProvider>
          </Box>

          <Box className={styles.fieldGroup}>
            <Typography className={styles.fieldLabel}>
              {Vocabulary.message}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              value={smsMessage}
              onChange={(event) => {
                setSmsMessage(event.target.value.slice(0, 160));
              }}
              placeholder={Vocabulary.smsMessagePlaceholder}
              inputProps={{ maxLength: 160 }}
              helperText={`${smsMessage.length}/160`}
            />
          </Box>

          {isExamMessage ? (
            <Box className={styles.actions}>
              {clientIdTimeMap.length === 0 && (
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
                  <TimePicker
                    value={examTime}
                    onChange={(time) => setExamTime(time)}
                    ampm={false}
                    slotProps={{
                      textField: { size: "medium", fullWidth: true },
                    }}
                  />
                </LocalizationProvider>
              )}
              <Button
                variant="contained"
                onClick={clientIdTimeMap.length > 0 ? handleSendSmsGroupedByTime : handleSendSmsWithTime}
                disabled={isSending || (clientIdTimeMap.length === 0 && !examTime)}
                fullWidth
              >
                {Vocabulary.sendMessageWithTime}
              </Button>
            </Box>
          ) : (
            <Box className={styles.actions}>
              <Button
                variant="outlined"
                onClick={handleApplyFilters}
                aria-label={Vocabulary.showClients}
              >
                {Vocabulary.showClients}
              </Button>
              <Button
                variant="contained"
                onClick={handleSendSms}
                disabled={!canSend}
                aria-label={Vocabulary.send}
              >
                {Vocabulary.send}
              </Button>
            </Box>
          )}

          <Button
            variant="outlined"
            fullWidth
            onClick={() => setExamFileModalOpen(true)}
          >
            {Vocabulary.uploadExamScheduleFile}
          </Button>

          <GenericModal
            open={examFileModalOpen}
            onClose={() => { setExamFileModalOpen(false); setExamFile([]); }}
            loading={isVerifying}
            title={Vocabulary.uploadExamScheduleFile}
            actions={
              <Button
                variant="contained"
                onClick={handleVerifyClients}
                disabled={!examFile.length}
              >
                {Vocabulary.verifyClients}
              </Button>
            }
          >
            <DropzoneComponent
              acceptedFileTypes={{ "application/pdf": [".pdf"] }}
              maxFiles={1}
              multiple={false}
              paths={examFile}
              onSave={(files) => setExamFile(files)}
            />
          </GenericModal>
        </div>

        <div className={styles.rightPanel}>
          {clientIdTimeMap.length > 0 ? (
            <ExamClientReviewPanel
              entries={clientIdTimeMap}
              onChange={setClientIdTimeMap}
            />
          ) : (
            <MUIDataTableCustomStyle
              title={Vocabulary.clients}
              columns={clientsColumns}
              CRUDEndPoint={endpoints.ordersEndpoint}
              onEditAction={ETableEditAction.NONE}
              hideAddButton
              hideEditButton
              hideDeleteButton
              selectableRows="none"
              filters={filters}
              triggerReload={triggerReload}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default withRole(SmsClientsPage);
